import { useNavigate } from "react-router-dom";
import { truncate } from "lodash";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import { Badge } from "@/app/admin/_common/ui/badge";
import { Skeleton } from "@/app/admin/_common/ui/skeleton";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import { cn } from "@/app/_utils/cn";
import { floatFormat } from "@/app/_utils/utils";
import { formatUtcDate } from "@/app/_utils/dayjs";
import { Receipt, ChevronLeft, ChevronRight } from "lucide-react";
import { PAGE_SIZE } from "../promoDetailPageModel";

const COLUMN_COUNT = 8;

export default function PromoOrdersTable({
  orders,
  count,
  page,
  isLoading,
  onGoToPage,
}: {
  orders: any[];
  count: number;
  page: number;
  isLoading: boolean;
  onGoToPage: (page: number) => void;
}) {
  const navigate = useNavigate();

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const rangeStart = count === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(count, page * PAGE_SIZE + PAGE_SIZE);

  const headClass =
    "h-8 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[900px] table-fixed", orders.length === 0 && !isLoading && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className={cn(headClass, "w-[9%]")}>Order</TableHead>
            <TableHead className={cn(headClass, "w-[15%]")}>Guest</TableHead>
            <TableHead className={cn(headClass, "w-[17%]")}>Email</TableHead>
            <TableHead className={cn(headClass, "w-[21%]")}>Show</TableHead>
            <TableHead className={cn(headClass, "w-[9%] text-center")}>Tickets</TableHead>
            <TableHead className={cn(headClass, "w-[10%] text-right")}>Discount</TableHead>
            <TableHead className={cn(headClass, "w-[9%] text-right")}>Paid</TableHead>
            <TableHead className={cn(headClass, "w-[13%]")}>Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <TableRow key={i} className="hover:bg-transparent">
                {Array.from({ length: COLUMN_COUNT }).map((_, j) => (
                  <TableCell key={j} className="py-2">
                    <Skeleton className="h-3 w-20" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : orders.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={COLUMN_COUNT} className="h-full p-0">
                {/* The table sits in a scrolling page body rather than a flex-fill
                    container, so `h-full` collapses to content height — the min-height
                    keeps the empty state roughly the size of a loaded page. */}
                <div className="flex min-h-[340px] flex-col justify-center">
                  <CuoEmpty icon={Receipt} text="No orders" />
                </div>
              </TableCell>
            </TableRow>
          ) : (
            orders.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="whitespace-nowrap py-1.5 text-[12.5px] text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    #{row.order_number ?? "—"}
                    {row.refunded && (
                      <Badge
                        variant="outline"
                        className="gap-1.5 rounded-[7px] border-amber-200 bg-amber-50 font-normal text-amber-700"
                      >
                        <span className="size-1.5 flex-none rounded-full bg-amber-500" />
                        Refunded
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="truncate py-1.5 text-[12.5px]">
                  {row.guest_data
                    ? `${row.guest_data.first_name ?? ""} ${row.guest_data.last_name ?? ""}`.trim() || "—"
                    : "—"}
                </TableCell>
                <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                  {row.guest_data?.email || "—"}
                </TableCell>
                <TableCell className="py-1.5 text-[12.5px]">
                  {row.show_data ? (
                    <span
                      className="cursor-pointer truncate hover:underline"
                      onClick={() => navigate(`/admin/shows/${row.show_data.id}/details`)}
                    >
                      {truncate(row.show_data.title, { length: 40 }) || "—"}
                    </span>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-center text-[12.5px] text-muted-foreground">
                  {row.quantity ?? 0}
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-right text-[12.5px] text-muted-foreground">
                  {floatFormat(row.discount_amount ?? 0)}
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-right text-[12.5px]">
                  {floatFormat(parseFloat(row.price))}
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-[12.5px] text-muted-foreground">
                  {formatUtcDate(row.created_at)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between border-t border-border px-3 py-1.5 text-[12px] text-muted-foreground">
        <span>{count === 0 ? "No orders" : `Showing ${rangeStart}–${rangeEnd} of ${count}`}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 0}
            onClick={() => onGoToPage(page - 1)}
            className="flex size-6 items-center justify-center rounded-md hover:bg-muted disabled:pointer-events-none disabled:opacity-30"
            aria-label="Previous page"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={() => onGoToPage(page + 1)}
            className="flex size-6 items-center justify-center rounded-md hover:bg-muted disabled:pointer-events-none disabled:opacity-30"
            aria-label="Next page"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
